import {
  applyEnumResolveToRow,
  applyImportDefaultsToRow,
  type EnumResolveEntry,
  type ImportDefault,
} from '@/shared/import'

export type TabularMapTarget =
  | { kind: 'attr'; name: string }
  | { kind: 'ref'; attr: string; lookupField: string }
  | { kind: 'skip' }

export type TabularImportWarning = {
  row: number
  attr: string
  message: string
}

export type TabularColumnMeta = {
  name: string
  attr_type: string
  target?: string
}

export type ResolveMapEntry = {
  id: string | null
  status: 'ok' | 'missing' | 'ambiguous'
}

const STRINGISH_ATTR_TYPES = new Set(['string', 'text', 'uuid', 'enum_ref'])

/** Excel/pandas often emits whole numbers as number; string columns need str. */
export function coerceTabularCellValue(
  value: unknown,
  attrType?: string,
): unknown {
  if (value === undefined || value === null || typeof value === 'boolean') {
    return value
  }
  const t = (attrType || 'string').toLowerCase()
  const stringish = STRINGISH_ATTR_TYPES.has(t) || !attrType
  if (!stringish || typeof value !== 'number' || !Number.isFinite(value)) {
    return value
  }
  if (Number.isInteger(value)) return String(value)
  // 1010.0 from Excel → "1010"
  if (Math.floor(value) === value && Math.abs(value) < 1e15) {
    return String(Math.trunc(value))
  }
  return String(value)
}

function coerceLookupKey(value: unknown): string {
  const coerced = coerceTabularCellValue(value, 'string')
  if (coerced === undefined || coerced === null) return ''
  return String(coerced).trim()
}

function resolveMapKey(attr: string, lookupField: string): string {
  return `${attr}|${lookupField}`
}

export function heuristicTabularMapping(
  headers: string[],
  columns: TabularColumnMeta[],
): Record<string, TabularMapTarget> {
  const byNameLower = new Map(
    columns.map((col) => [col.name.toLowerCase(), col]),
  )
  const result: Record<string, TabularMapTarget> = {}
  for (const header of headers) {
    const match = byNameLower.get(header.toLowerCase())
    if (match) {
      result[header] = { kind: 'attr', name: match.name }
    } else {
      result[header] = { kind: 'skip' }
    }
  }
  return result
}

export type BuildTabularImportRowsArgs = {
  fileRows: Record<string, unknown>[]
  mapping: Record<string, TabularMapTarget>
  resolveMaps: Record<string, Record<string, ResolveMapEntry>>
  attrTypes: Record<string, string>
  refAttrs?: Set<string>
  mapColumnOrder: string[]
  enumMaps?: Record<string, Record<string, EnumResolveEntry>>
  enumAttrs?: string[]
  defaults?: ImportDefault[]
}

export function buildTabularImportRows(
  args: BuildTabularImportRowsArgs,
): { rows: Record<string, unknown>[]; warnings: TabularImportWarning[] } {
  const {
    fileRows,
    mapping,
    resolveMaps,
    attrTypes,
    mapColumnOrder,
    enumMaps = {},
    enumAttrs = [],
    defaults = [],
  } = args
  const warnings: TabularImportWarning[] = []
  const rows: Record<string, unknown>[] = []

  const refEntriesByAttr = new Map<
    string,
    { column: string; lookupField: string }[]
  >()
  for (const column of mapColumnOrder) {
    const target = mapping[column]
    if (!target || target.kind !== 'ref') continue
    const list = refEntriesByAttr.get(target.attr) ?? []
    list.push({ column, lookupField: target.lookupField })
    refEntriesByAttr.set(target.attr, list)
  }

  for (let index = 0; index < fileRows.length; index += 1) {
    const fileRow = fileRows[index]
    const rowNum =
      typeof fileRow._row === 'number' ? fileRow._row : index + 2
    const row: Record<string, unknown> = {}

    for (const column of mapColumnOrder) {
      const target = mapping[column]
      if (!target || target.kind === 'skip' || target.kind === 'ref') continue
      const attrType = attrTypes[target.name]
      row[target.name] = coerceTabularCellValue(fileRow[column], attrType)
    }

    for (const [attr, entries] of Array.from(refEntriesByAttr)) {
      let resolvedId: string | null = null
      let found = false

      for (const { column, lookupField } of entries) {
        const lookupKey = coerceLookupKey(fileRow[column])
        const mapKey = resolveMapKey(attr, lookupField)
        const resolveMap = resolveMaps[mapKey] ?? {}
        const hit = lookupKey ? resolveMap[lookupKey] : undefined

        if (!hit || hit.status === 'missing') {
          warnings.push({
            row: rowNum,
            attr,
            message: `Missing match for "${lookupKey}" on ${attr} via ${lookupField}`,
          })
          continue
        }

        resolvedId = hit.id
        if (hit.status === 'ambiguous') {
          warnings.push({
            row: rowNum,
            attr,
            message: `Ambiguous match for "${lookupKey}" on ${attr} via ${lookupField}`,
          })
        }
        found = true
        break
      }

      row[attr] = found && resolvedId != null ? resolvedId : ''
    }

    let finalRow = row
    if (enumAttrs.length > 0) {
      const enumResult = applyEnumResolveToRow(finalRow, enumAttrs, enumMaps, rowNum)
      finalRow = enumResult.row
      warnings.push(...enumResult.warnings)
    }

    if (defaults.length > 0) {
      const defaultsResult = applyImportDefaultsToRow(
        finalRow,
        defaults,
        { refMaps: resolveMaps, enumMaps },
        rowNum,
      )
      finalRow = defaultsResult.row
      warnings.push(...defaultsResult.warnings)
    }

    rows.push(finalRow)
  }

  return { rows, warnings }
}

export function assignLineNumbers(
  rows: Record<string, unknown>[],
  startFrom: number,
): Record<string, unknown>[] {
  return rows.map((row, index) => ({
    ...row,
    line_number: startFrom + index,
  }))
}

export function mergeTabularImport(
  existing: Record<string, unknown>[],
  draft: Record<string, unknown>[],
  mode: 'append' | 'replace',
): Record<string, unknown>[] {
  if (mode === 'replace') {
    return draft.map((row) => ({ ...row }))
  }
  return [...existing.map((row) => ({ ...row })), ...draft.map((row) => ({ ...row }))]
}
